import React, { useMemo, useState } from 'react';

import { ActionDropdown, Pagination } from '@/components';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { SearchField } from '@/design-system/search-field';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { warehouseApi } from '../../api/warehouse.api';
import { EmptyTable } from '../../components/ListShell';
import { SkuDetailPanel } from '../../components/SkuDetailPanel';
import { SkuImportModal } from '../../components/SkuImportModal';
import { SkuFormModal } from '../../components/SkuModals';
import {
  codeCls,
  Dash,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  numCls,
  PageHeader,
} from '../../components/ui';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { SKU_FILTERS } from '../../filterFields';
import { fmtQty } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import {
  NO_TABS,
  PAGE_SIZE_OPTIONS,
  useListQuery,
} from '../../hooks/useListQuery';
import { SKU_STATUS_LABEL } from '../../skuMaster';
import type { ClientRef, WarehouseSku } from '../../types';
import { useWarehouse } from '../../WarehouseContext';

const SKU_STATUS_META = {
  ACTIVE: { label: SKU_STATUS_LABEL.ACTIVE, tone: 'success' },
  INACTIVE: { label: SKU_STATUS_LABEL.INACTIVE, tone: 'faded' },
} as const;

const columnsOf = (
  open: (s: WarehouseSku) => void,
  edit: (s: WarehouseSku) => void
): TableDataColumn<WarehouseSku>[] => [
  {
    key: 'sku',
    title: 'SKU Code',
    render: (_v, s) => <span className={codeCls}>{s.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'SKU Name',
    render: (_v, s) => (
      <div>
        <p className={nameCls}>{s.itemName}</p>
        {s.barcode && <p className={`${metaCls} font-mono`}>{s.barcode}</p>}
      </div>
    ),
  },
  {
    key: 'brand',
    title: 'Brand',
    render: (_v, s) =>
      s.brand ? <span className={nameCls}>{s.brand}</span> : <Dash />,
  },
  {
    key: 'client',
    title: 'Client',
    render: (_v, s) => (
      <span className={nameCls}>
        {s.client?.name ?? `Client #${s.clientID}`}
      </span>
    ),
  },
  {
    key: 'category',
    title: 'Category / Type',
    render: (_v, s) =>
      s.productCategory || s.productType ? (
        <div>
          <p className={nameCls}>{s.productCategory ?? '—'}</p>
          {s.productType && <p className={metaCls}>{s.productType}</p>}
        </div>
      ) : (
        <Dash />
      ),
  },
  {
    key: 'uomName',
    title: 'UOM',
    render: (_v, s) => <span className={metaCls}>{s.uomName}</span>,
  },
  {
    key: 'weight',
    title: 'Weight / PCS',
    align: 'right',
    render: (_v, s) => (
      <span className={numCls}>
        {s.weightKg ? `${fmtQty(s.weightKg)} kg` : '—'}
      </span>
    ),
  },
  {
    key: 'skuStatus',
    title: 'Status',
    render: (_v, s) => (
      <MappedBadge map={SKU_STATUS_META} value={s.status ?? 'ACTIVE'} />
    ),
  },
  {
    key: 'actions',
    title: '',
    align: 'right',
    render: (_v, s) => (
      <ActionDropdown
        ariaLabel={`Aksi ${s.sku}`}
        actions={[
          {
            key: 'view',
            label: 'Lihat detail',
            icon: 'ri-eye-line',
            onClick: () => open(s),
          },
          {
            key: 'edit',
            label: 'Edit',
            icon: 'ri-edit-line',
            onClick: () => edit(s),
          },
        ]}
      />
    ),
  },
];

/** SKU catalog (interim): list + create + CSV import per client. */
const SkuPage: React.FC = () => {
  const { refreshToken, refresh } = useWarehouse();
  const q = useListQuery(NO_TABS, 'ALL', SKU_FILTERS);
  const { api: filterApi, apiKey: filterKey, values } = q.filters;
  const search = q.search.committed || null;
  const { page, limit } = q;
  const [modal, setModal] = useState<'create' | 'import' | null>(null);
  const [detail, setDetail] = useState<WarehouseSku | null>(null);
  const [editing, setEditing] = useState<WarehouseSku | null>(null);
  const columns = useMemo(
    () =>
      columnsOf(setDetail, (s) => {
        setDetail(null);
        setEditing(s);
      }),
    []
  );
  // The filtered client prefills the create / import modals (memoised: the
  // modals reset when it changes).
  const client = useMemo<ClientRef | null>(
    () =>
      values.clientID
        ? {
            id: Number(values.clientID),
            name: values.clientName || `Client #${values.clientID}`,
          }
        : null,
    [values.clientID, values.clientName]
  );

  // Server paging + search (SKU code or item name) — never the whole catalog.
  const skus = useLoad(
    (signal) =>
      warehouseApi.listSkus({ ...filterApi, search, page, limit }, { signal }),
    [filterKey, search, page, limit, refreshToken]
  );
  const rows = skus.data?.data ?? [];
  const total = skus.data?.pagination.total ?? 0;

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Warehouse management"
        title="SKU"
        actions={
          <>
            <Button
              tone="neutral"
              style="stroke"
              onClick={refresh}
              leftIcon={<i className="ri-refresh-line text-[14px]" />}
            >
              Refresh
            </Button>
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setModal('import')}
              leftIcon={<i className="ri-upload-2-line text-[14px]" />}
            >
              Import CSV
            </Button>
            <Button
              onClick={() => setModal('create')}
              leftIcon={<i className="ri-add-line text-[14px]" />}
            >
              Tambah SKU
            </Button>
          </>
        }
      />
      <Card padding="none">
        <div className="flex flex-wrap items-center justify-end gap-2 border-b border-stroke-soft-200 px-4 py-3">
          <SearchField
            size="sm"
            value={q.search.value}
            onChange={q.search.setValue}
            debounceMs={0}
            placeholder="Cari SKU, nama, brand, atau barcode…"
            className="w-64"
          />
          <WarehouseFilterPanel filters={q.filters} title="Filter SKU" />
        </div>
        <div className="p-4">
          {skus.error ? (
            <LoadError message={skus.error.message} onRetry={skus.reload} />
          ) : (
            <>
              <TableData
                size="sm"
                loading={skus.loading}
                data={rows}
                columns={columns}
                onRowClick={setDetail}
                emptyState={<EmptyTable title="Belum ada SKU" />}
              />
              {total > 0 && (
                <Pagination
                  currentPage={page}
                  totalItems={total}
                  itemsPerPage={limit}
                  onPageChange={q.setPage}
                  onPageSizeChange={q.setLimit}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                />
              )}
            </>
          )}
        </div>
      </Card>
      <SkuFormModal
        open={modal === 'create' || !!editing}
        sku={editing}
        initialClient={client}
        onClose={() => {
          setModal(null);
          setEditing(null);
        }}
        onSaved={(s, mode) => {
          setModal(null);
          setEditing(null);
          toast.success(mode === 'edit' ? 'SKU disimpan' : 'SKU dibuat', {
            description: `${s.sku} · ${s.itemName}`,
          });
          refresh();
        }}
      />
      <SkuDetailPanel
        sku={detail}
        onClose={() => setDetail(null)}
        onEdit={(s) => {
          setDetail(null);
          setEditing(s);
        }}
      />
      <SkuImportModal
        open={modal === 'import'}
        initialClient={client}
        onClose={() => setModal(null)}
        onImported={(r) => {
          setModal(null);
          toast.success(`${r.created} SKU di-import`, {
            description: r.skippedDuplicates
              ? `${r.skippedDuplicates} dilewati (sudah ada)`
              : undefined,
          });
          refresh();
        }}
      />
    </div>
  );
};

export default SkuPage;
